import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../lib/firebase';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    sendEmailVerification,
    sendPasswordResetEmail,
} from 'firebase/auth';

const AuthContext = createContext();

export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [quota, setQuota] = useState(null);

    const fetchQuota = async (user) => {
        if (!user) return;
        try {
            console.log("Fetching quota for user:", user.email);
            // Get ID token, force refresh to ensure it's valid
            const token = await user.getIdToken(true);
            const apiUrl = process.env.REACT_APP_API_URL || '';
            console.log("Using API URL:", apiUrl);

            const response = await fetch(`${apiUrl}/api/user/quota`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                console.log("Quota fetched successfully:", data.quota);
                setQuota(data.quota);
            } else {
                console.error("Failed to fetch quota, status:", response.status);
                const errorText = await response.text();
                console.error("Error details:", errorText);
            }
        } catch (error) {
            console.error("Failed to fetch quota (network/other):", error);
        }
    };

    // Listen for Firebase auth state changes
    useEffect(() => {
        // Check if auth is available (not null)
        if (auth) {
            const unsubscribe = onAuthStateChanged(auth, async (user) => {
                setCurrentUser(user);
                if (user) {
                    await fetchQuota(user);
                } else {
                    setQuota(null);
                }
                setLoading(false);
            });

            const interval = setInterval(() => {
                if (auth.currentUser) {
                    auth.currentUser.reload();
                }
            }, 5000);

            return () => {
                unsubscribe();
                clearInterval(interval);
            };
        } else {
            // If no auth object, set loading to false immediately
            setLoading(false);
            return () => { };
        }
    }, []);

    // Firebase authentication functions
    const signup = (email, password) => {
        if (!auth) {
            return Promise.reject(new Error('Authentication is not available. Firebase is not properly configured.'));
        }
        return createUserWithEmailAndPassword(auth, email, password);
    };

    const signin = (email, password) => {
        if (!auth) {
            return Promise.reject(new Error('Authentication is not available. Firebase is not properly configured.'));
        }
        return signInWithEmailAndPassword(auth, email, password);
    };

    const signout = () => {
        if (!auth) {
            return Promise.reject(new Error('Authentication is not available. Firebase is not properly configured.'));
        }
        setQuota(null);
        return signOut(auth);
    };

    const sendVerificationEmail = () => {
        if (!auth.currentUser) {
            return Promise.reject(new Error('No user is currently signed in.'));
        }
        return sendEmailVerification(auth.currentUser);
    };

    const resetPassword = (email) => {
        if (!auth) {
            return Promise.reject(new Error('Authentication is not available. Firebase is not properly configured.'));
        }
        return sendPasswordResetEmail(auth, email);
    };

    const value = {
        currentUser,
        quota,
        fetchQuota: () => fetchQuota(currentUser),
        signup,
        signin,
        signout,
        sendVerificationEmail,
        resetPassword,
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
